import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './OnboardingHint.css';

function OnboardingHint({
  anchorRef,
  title = 'Welcome',
  message,
  onClose,
  onSkipAll,
  placement = 'bottom',
  ctaLabel = 'Got it',
}) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPos, setArrowPos] = useState('top');
  const popoverRef = useRef(null);

  const updatePosition = () => {
    const anchor = anchorRef?.current;
    const pop = popoverRef.current;
    if (!anchor || !pop) return;

    const rect = anchor.getBoundingClientRect();
    const popRect = pop.getBoundingClientRect();
    let top = rect.bottom + 12;
    let left = rect.left + rect.width / 2 - popRect.width / 2;
    let arrow = 'top';

    if (placement === 'top') {
      top = rect.top - popRect.height - 12;
      arrow = 'bottom';
    } else if (placement === 'left') {
      top = rect.top + rect.height / 2 - popRect.height / 2;
      left = rect.left - popRect.width - 12;
      arrow = 'right';
    } else if (placement === 'right') {
      top = rect.top + rect.height / 2 - popRect.height / 2;
      left = rect.right + 12;
      arrow = 'left';
    }

    // Keep within viewport horizontally
    const margin = 8;
    if (left < margin) left = margin;
    const maxLeft = window.innerWidth - popRect.width - margin;
    if (left > maxLeft) left = maxLeft;

    const nextTop = Math.max(margin, top);
    const nextLeft = left;
    setPosition((prev) => (prev.top !== nextTop || prev.left !== nextLeft ? { top: nextTop, left: nextLeft } : prev));
    setArrowPos((prev) => (prev !== arrow ? arrow : prev));
  };

  useLayoutEffect(() => {
    // Position once after mount, then rely on resize/scroll listeners
    requestAnimationFrame(() => updatePosition());
  }, []);

  useEffect(() => {
    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    // Also observe anchor movement via ResizeObserver if available
    const ro = anchorRef?.current && 'ResizeObserver' in window ? new ResizeObserver(() => updatePosition()) : null;
    if (ro && anchorRef.current) ro.observe(anchorRef.current);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      if (ro && anchorRef.current) ro.unobserve(anchorRef.current);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="onboarding-backdrop" onClick={onClose} />
      <div
        ref={popoverRef}
        className={`onboarding-popover arrow-${arrowPos}`}
        style={{ top: position.top, left: position.left }}
        role="dialog"
        aria-live="polite"
      >
        <div className="onboarding-title">{title}</div>
        <div className="onboarding-message">{message}</div>
        <div className="onboarding-actions">
          <button className="onboarding-cta" onClick={onClose}>{ctaLabel}</button>
          {onSkipAll && (
            <button className="onboarding-skip" onClick={onSkipAll}>Skip all</button>
          )}
        </div>
      </div>
    </>
  );
}

export default OnboardingHint;


